import React, { useEffect, useState } from "react";
import { Box, Flex } from "theme-ui";
import { useNavigate } from "react-router-dom";
import { getProjectFiles } from "api/Projects";
import { formatDateOnly } from "helpers/dateOnly";
import { themevals } from "theme/themevals";

/**
 * "FROM THE CLIENT" - STAFF VIEW (2026-10-12, Jack: will we see what they
 * added?). The staff twin of the client's Next steps card: what the client
 * gave us after accepting - their date needed by (and whether we've
 * confirmed it with a Scheduled Date), PO number, and labeled files. Only
 * client-labeled files count (staff uploads on the Files tab have no kind).
 * Hidden when the client has given nothing. Slack gets the same events.
 */
const ProjectClientInputs: React.FC<{ project: any }> = ({ project }) => {
  const navigate = useNavigate();
  const [files, setFiles] = useState<any[]>([]);

  useEffect(() => {
    if (!project?.id) return;
    let live = true;
    void getProjectFiles({ data: { id: project.id } }).then((res: any) => {
      if (live) setFiles(Array.isArray(res) ? res.filter((f: any) => !!f?.kind) : []);
    });
    return () => {
      live = false;
    };
  }, [project?.id]);

  const count = (k: string) => files.filter((f) => f.kind === k).length;
  const parts = [
    count("po") ? `${count("po")} PO` : null,
    count("site_map") ? `${count("site_map")} site map${count("site_map") > 1 ? "s" : ""}` : null,
    count("photo") ? `${count("photo")} site photo${count("photo") > 1 ? "s" : ""}` : null,
    count("other") ? `${count("other")} other` : null,
  ].filter(Boolean) as string[];

  const needed = project?.clientNeededBy;
  const scheduled = project?.scheduleDate;
  if (!needed && !project?.clientPO && !parts.length) return null;

  const neededDay = needed ? String(needed).slice(0, 10) : null;
  const scheduledDay = scheduled ? formatDateOnly(scheduled, "YYYY-MM-DD") : null;
  const late = neededDay && scheduledDay && scheduledDay > neededDay;

  const chip = (label: React.ReactNode, tone: "neutral" | "green" | "amber" = "neutral", onClick?: () => void) => (
    <Box
      as="span"
      onClick={onClick}
      sx={{
        display: "inline-flex", alignItems: "center", fontSize: "12px", padding: "3px 10px", borderRadius: "999px", cursor: onClick ? "pointer" : "default",
        backgroundColor: tone === "green" ? "#E6F2DA" : tone === "amber" ? "#FDF0DA" : "#EEF0F2",
        color: tone === "green" ? "#3B6D11" : tone === "amber" ? "#8A5300" : "#4A5560",
      }}
    >
      {label}
    </Box>
  );

  return (
    <Flex sx={{ border: "1px solid " + themevals.colors.inputOutline, borderRadius: "8px", padding: "10px 16px", marginBottom: "20px", gap: "8px", alignItems: "center", flexWrap: "wrap", fontFamily: "Poppins, sans-serif" }}>
      <Box sx={{ fontSize: "13px", fontWeight: 600, color: "#111", marginRight: "4px" }}>From the client</Box>
      {neededDay &&
        chip(
          <>
            {"Needs by "}<strong style={{ marginLeft: 4 }}>{formatDateOnly(needed, "ddd, MMM D")}</strong>
            {!scheduledDay ? " · not scheduled yet" : late ? ` · scheduled after (${formatDateOnly(scheduled, "MMM D")})` : " · scheduled in time"}
          </>,
          !scheduledDay || late ? "amber" : "green",
        )}
      {project?.clientPO && chip(<>{"PO# "}<strong style={{ marginLeft: 4 }}>{project.clientPO}</strong></>)}
      {parts.length > 0 && chip(parts.join(" · ") + " →", "neutral", () => navigate(`/projects/${project.id}?tab=files`))}
    </Flex>
  );
};

export default ProjectClientInputs;
